import { Button, Typography, Stack, Box, CircularProgress, Tooltip } from "@mui/material";
import { ConnectionDto, ConnectionName, mapConnectionUIName } from "@arelia-types/connection";
import { PropsWithChildren, useEffect, useState, useContext } from "react";
import { openAuthWindow } from "../helpers";
import BasicDialog from "./BasicDialog";
import FacebookLoginButton from "@arelia-components/ui-atoms/FacebookLoginButton";
import { useMetaPages } from "../../../../api/metaPages";
import InstagramIcon from "@mui/icons-material/Instagram";
import LogoutIcon from "@mui/icons-material/Logout";
import { getPostForMeAuthUrl, fetchAccounts, disconnectAllPlatform } from "../../../social/post/util";
import openNewWindow, { waitForWindowClose } from "@arelia-utils/helpers/openNewWindow";
import { PostForMeAccount } from "../../../social/post/models";
import { useConfiguration } from "../../../../api/configuration";
import AppContext from "src/AppContext";
import { SubscriptionPlanName } from "@arelia-types/subscriptions";

type Props = {
  connection: ConnectionDto,
  onClose: () => void,
  onConnect?: () => void,
  onPostForMeChange?: () => void
}

export default function MetaDialog({
  connection,
  onClose,
  onPostForMeChange
}: PropsWithChildren<Props>) {
  const pagesQuery = useMetaPages(connection.name);
  const isInstagram = connection.name === ConnectionName.instagram;
  const { data: configuration } = useConfiguration();
  const { account } = useContext(AppContext);

  const isPostingFeatureEnabled = configuration?.flags?.EnablePosting === true;
  const planSupportsMySocial = [SubscriptionPlanName.MySocial, SubscriptionPlanName.MySocialMyFans].includes(account?.plan as SubscriptionPlanName);

  const [postForMeAccounts, setPostForMeAccounts] = useState<PostForMeAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingAccounts, setIsFetchingAccounts] = useState(isInstagram && isPostingFeatureEnabled);

  useEffect(() => {
    if (isInstagram && isPostingFeatureEnabled) {
      setIsFetchingAccounts(true);
      fetchAccounts()
        .then(setPostForMeAccounts)
        .catch(e => console.error("Failed to fetch PostForMe accounts:", e))
        .finally(() => setIsFetchingAccounts(false));
    }
  }, [isInstagram, isPostingFeatureEnabled, connection.connected]);

  const postForMeInstagramAccount = isInstagram
    ? postForMeAccounts.find(
        a => a.platform?.toLowerCase() === "instagram" &&
             (a.status ?? "").toLowerCase() === "connected"
      )
    : null;

  const isConnectedViaPostForMe = !!postForMeInstagramAccount;

  const handleInstagramPostForMeLogin = async () => {
    setIsLoading(true);
    try {
      const url = await getPostForMeAuthUrl("instagram", "instagram");
      const authWindow = openNewWindow(url, {
        width: 650,
        height: 750,
        left: 'center',
        top: 'center',
        resizable: 1,
        scrollbars: 1
      });
      if (authWindow) {
        await waitForWindowClose(authWindow);
        const accounts = await fetchAccounts();
        setPostForMeAccounts(accounts);
        pagesQuery.refetch();
        if (onPostForMeChange) {
          onPostForMeChange();
        }
      }
    } catch (error) {
      console.error("Failed to get Instagram auth URL:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInstagramPostForMeLogout = async () => {
    setIsLoading(true);
    try {
      await disconnectAllPlatform("instagram");
      const accounts = await fetchAccounts();
      setPostForMeAccounts(accounts);
      pagesQuery.refetch();
      if (onPostForMeChange) {
        onPostForMeChange();
      }
    } catch (error) {
      console.error("Failed to logout from Instagram:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isInstagram && isPostingFeatureEnabled && isFetchingAccounts && !connection.connected) {
    return (
      <BasicDialog
        connection={connection}
        title={`Sign in to ${connection.name}`}
        onClose={onClose}
        showSaveButton={false}
        canDisconnect={false}
        pagesQuery={pagesQuery}
        renderActions={() => <></>}
      >
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      </BasicDialog>
    );
  }

  return (
    <BasicDialog
      connection={connection}
      title={`Sign in to ${connection.name}`}
      onClose={onClose}
      showSaveButton={connection.connected && pagesQuery.data && pagesQuery.data.length > 0}
      canDisconnect={true}
      pagesQuery={pagesQuery}
      onPostForMeChange={onPostForMeChange}
      renderActions={() => (
        connection.connected
          ?
          <Button
            variant="outlined"
            onClick={() => openAuthWindow(connection.name)}
          >
            Reconnect
          </Button>
          : isInstagram && isPostingFeatureEnabled && isConnectedViaPostForMe
          ?
          <></>
          :
          <Stack direction="row" spacing={1}>
            <FacebookLoginButton onClick={() => openAuthWindow(connection.name)} />
            {isInstagram && isPostingFeatureEnabled && (
              <Tooltip title={!planSupportsMySocial ? "You need to upgrade your subscription to My Social or My Social + My Fans" : ""}>
                <span>
                  <Button
                    variant="contained"
                    onClick={handleInstagramPostForMeLogin}
                    startIcon={<InstagramIcon />}
                    disabled={isLoading || !planSupportsMySocial}
                    sx={styles.instagramButton}
                  >
                    {isLoading ? "Connecting..." : "Login with Instagram"}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
      )}
    >
      {!connection.connected
        ? isInstagram && isPostingFeatureEnabled && isConnectedViaPostForMe
          ?
          <Box>
            <Typography sx={{ mb: 2 }}>
              Arelia has successfully connected to Instagram with account <strong>{postForMeInstagramAccount?.username || "Unknown"}</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This connection supports posting only.
            </Typography>
            <Button
              variant="contained"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleInstagramPostForMeLogout}
              disabled={isLoading}
            >
              {isLoading ? "Disconnecting..." : "Disconnect"}
            </Button>
          </Box>
          :
          <Typography>
            Please sign in to your Meta account to establish connection {mapConnectionUIName(connection.name)} service with Arelia
          </Typography>
        :
        <>
          <Typography>Arelia has successfully connected to {mapConnectionUIName(connection.name)}{connection.login ? <> with account <b>{connection.login}</b></> : ''}.</Typography>
          {pagesQuery.data && pagesQuery.data?.length > 0 && <Typography sx={{ marginTop: "15px" }}></Typography>}
        </>
      }

    </BasicDialog>
  );
}

const styles = {
  instagramButton: {
    background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
    color: "white",
    borderRadius: 1,
    "&:hover": {
      background: "linear-gradient(45deg, #e08323 0%, #d6582c 25%, #cc1733 50%, #bc1356 75%, #ac0878 100%)",
    },
    "&.Mui-disabled": {
      background: "#ccc",
      color: "#666",
    }
  }
};
